import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Miseadispo } from './http/miseadispos.service';

export interface CartItem {
  miseadispo: Miseadispo;
  quantity: number;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private readonly CART_STORAGE_KEY = 'labase_cart';
  private cartItems = new BehaviorSubject<CartItem[]>([]);

  public cartItems$ = this.cartItems.asObservable();

  constructor() {
    this.loadCartFromSession();
  }

  /**
   * Charge le panier depuis le sessionStorage
   */
  private loadCartFromSession(): void {
    try {
      const cartData = sessionStorage.getItem(this.CART_STORAGE_KEY);
      if (cartData) {
        const items = JSON.parse(cartData) as CartItem[];
        this.cartItems.next(items);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du panier :', error);
      this.cartItems.next([]);
    }
  }

  /**
   * Sauvegarde le panier dans le sessionStorage
   */
  private saveCartToSession(): void {
    try {
      const items = this.cartItems.value;
      sessionStorage.setItem(this.CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du panier :', error);
    }
  }

  /**
   * Récupère tous les items du panier
   */
  getCartItems(): CartItem[] {
    return this.cartItems.value;
  }

  /**
   * Ajoute un produit au panier
   */
  addToCart(miseadispo: Miseadispo, quantity: number = 1): void {
    const currentItems = this.cartItems.value;
    const existingItem = currentItems.find(item => item.miseadispo.id === miseadispo.id);

    if (existingItem) {
      // Augmente la quantité si le produit existe déjà
      const newQuantity = existingItem.quantity + quantity;
      // Vérifie qu'on ne dépasse pas la quantité disponible
      if (newQuantity <= miseadispo.quantiteActuel) {
        existingItem.quantity = newQuantity;
      } else {
        existingItem.quantity = miseadispo.quantiteActuel;
      }
    } else {
      // Ajoute un nouvel item
      const limitedQuantity = Math.min(quantity, miseadispo.quantiteActuel);
      currentItems.push({ miseadispo, quantity: limitedQuantity });
    }

    this.cartItems.next(currentItems);
    this.saveCartToSession();
  }

  /**
   * Retire un produit du panier
   */
  removeFromCart(miseadispoId: string): void {
    const currentItems = this.cartItems.value.filter(
      item => item.miseadispo.id !== miseadispoId
    );
    this.cartItems.next(currentItems);
    this.saveCartToSession();
  }

  /**
   * Met à jour la quantité d'un produit dans le panier
   */
  updateQuantity(miseadispoId: string, quantity: number): void {
    const currentItems = this.cartItems.value;
    const item = currentItems.find(item => item.miseadispo.id === miseadispoId);

    if (item) {
      // Vérifie que la quantité est valide
      if (quantity <= 0) {
        this.removeFromCart(miseadispoId);
      } else if (quantity <= item.miseadispo.quantiteActuel) {
        item.quantity = quantity;
        this.cartItems.next(currentItems);
        this.saveCartToSession();
      } else {
        item.quantity = item.miseadispo.quantiteActuel;
        this.cartItems.next(currentItems);
        this.saveCartToSession();
      }
    }
  }

  /**
   * Vide complètement le panier
   */
  clearCart(): void {
    this.cartItems.next([]);
    sessionStorage.removeItem(this.CART_STORAGE_KEY);
  }

  /**
   * Compte le nombre total d'articles dans le panier
   */
  getTotalItems(): number {
    return this.cartItems.value.reduce((total, item) => total + item.quantity, 0);
  }

  /**
   * Vérifie si un produit est dans le panier
   */
  isInCart(miseadispoId: string): boolean {
    return this.cartItems.value.some(item => item.miseadispo.id === miseadispoId);
  }

  /**
   * Récupère la quantité d'un produit dans le panier
   */
  getItemQuantity(miseadispoId: string): number {
    const item = this.cartItems.value.find(item => item.miseadispo.id === miseadispoId);
    return item ? item.quantity : 0;
  }

  /**
   * Compte le nombre total d'articles dans le panier (alias de getTotalItems)
   */
  getItemCount(): number {
    return this.getTotalItems();
  }
}
